import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { useSnackbar } from 'notistack';
import DashboardLayout from '../layouts/DashboardLayout';
import sessionsApi from '../api/sessions';
import { listUsers } from '../api/users';
import SessionFormDialog from '../components/SessionFormDialog';
import { formatDateTime, formatDurationHours } from '../utils/format';

const statusOptions = [
  { value: 'all', label: 'All statuses' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in-progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const SessionsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [educators, setEducators] = useState([]);
  const [students, setStudents] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  const loadUsers = useCallback(async () => {
    try {
      const [educatorList, studentList] = await Promise.all([
        listUsers({ role: 'educator' }),
        listUsers({ role: 'student' }),
      ]);
      setEducators(educatorList.map((user) => ({
        value: user._id,
        label: `${user.name} ${user.surname}`,
      })));
      setStudents(studentList.map((user) => ({
        value: user._id,
        label: `${user.name} ${user.surname} (Grade ${user.grade ?? 'N/A'})`,
      })));
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to load people directory', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  const loadSessions = useCallback(async () => {
    try {
      setLoading(true);
      const params = statusFilter === 'all' ? {} : { status: statusFilter };
      const data = await sessionsApi.listSessions(params);
      setRows(data);
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to load sessions', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, statusFilter]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const handleCreate = () => {
    setSelectedSession(null);
    setDialogOpen(true);
  };

  const handleEdit = (row) => {
    setSelectedSession({
      ...row,
      educatorId: row.educatorId?._id || row.educatorId,
      studentIds: (row.studentIds || []).map((student) => (student._id || student)),
    });
    setDialogOpen(true);
  };

  const handleDelete = async (row) => {
    const confirmed = window.confirm(`Delete session "${row.title}" This cannot be undone.`);
    if (!confirmed) return;
    try {
      await sessionsApi.deleteSession(row._id);
      enqueueSnackbar('Session deleted', { variant: 'success' });
      loadSessions();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to delete session', { variant: 'error' });
    }
  };

  const handleDialogSubmit = async (payload) => {
    try {
      setDialogLoading(true);
      if (selectedSession?._id) {
        await sessionsApi.updateSession(selectedSession._id, payload);
        enqueueSnackbar('Session updated', { variant: 'success' });
      } else {
        await sessionsApi.createSession(payload);
        enqueueSnackbar('Session created', { variant: 'success' });
      }
      setDialogOpen(false);
      loadSessions();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to save session', { variant: 'error' });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleSyncAttendance = async (row) => {
    try {
      await sessionsApi.syncBbbSession(row._id);
      enqueueSnackbar('Attendance sync triggered', { variant: 'success' });
      loadSessions();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to sync attendance', { variant: 'error' });
    }
  };

  const columns = useMemo(() => [
    { field: 'title', headerName: 'Title', flex: 1, minWidth: 200 },
    {
      field: 'subject',
      headerName: 'Subject',
      minWidth: 160,
      valueGetter: (params) => params.row.subject || '-',
    },
    {
      field: 'educator',
      headerName: 'Educator',
      minWidth: 200,
      valueGetter: (params) => {
        const educator = params.row.educatorId;
        if (!educator) return '-';
        if (typeof educator === 'string') {
          const match = educators.find((option) => option.value === educator);
          return match?.label || educator;
        }
        return `${educator.name} ${educator.surname}`;
      },
    },
    {
      field: 'studentCount',
      headerName: 'Students',
      minWidth: 110,
      valueGetter: (params) => params.row.studentIds?.length || 0,
    },
    {
      field: 'startTime',
      headerName: 'Start',
      minWidth: 190,
      valueGetter: (params) => formatDateTime(params.row.startTime),
    },
    {
      field: 'endTime',
      headerName: 'End',
      minWidth: 190,
      valueGetter: (params) => formatDateTime(params.row.endTime),
    },
    {
      field: 'duration',
      headerName: 'Duration',
      minWidth: 140,
      valueGetter: (params) => formatDurationHours(params.row.startTime, params.row.endTime),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 140,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={
            params.value === 'completed' ?
              'success'
              : params.value === 'in-progress' ?
                'warning'
                : params.value === 'cancelled' ?
                  'default'
                  : 'primary'
          }
          size="small"
        />
      ),
    },
    {
      field: 'attendance',
      headerName: 'Attendance',
      minWidth: 220,
      valueGetter: (params) => params.row.attendanceSummary,
      renderCell: (params) => {
        const summary = params.row.attendanceSummary || {};
        const syncedAt = summary.lastSyncedAt ? formatDateTime(summary.lastSyncedAt) : 'Not synced';
        const present = summary.presentCount ?? 0;
        const absent = summary.absentCount ?? 0;
        return (
          <Stack spacing={0.5}>
            <Typography variant="caption">{`Present: ${present} | Absent: ${absent}`}</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                label={summary.lastSyncStatus || 'pending'}
                color={summary.lastSyncStatus === 'success' ? 'success' : summary.lastSyncStatus === 'failed' ? 'error' : 'default'}
              />
              <Typography variant="caption" color="text.secondary">
                {syncedAt}
              </Typography>
            </Stack>
          </Stack>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      minWidth: 200,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Sync attendance from BBB">
            <IconButton size="small" color="primary" onClick={() => handleSyncAttendance(params.row)}>
              <SyncRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" color="primary" onClick={() => handleEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDelete(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ], [educators]);

  return (
    <DashboardLayout
      title="Sessions"
      actions={(
        <Stack direction="row" spacing={1}>
          <TextField
            select
            size="small"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            sx={{ minWidth: 160 }}
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Tooltip title="Refresh">
            <IconButton onClick={loadSessions}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button startIcon={<AddIcon />} variant="contained" onClick={handleCreate}>
            Schedule Session
          </Button>
        </Stack>
      )}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
                    Upcoming & Recent Sessions
                  </Typography>
                  <Chip icon={<PlayCircleFilledWhiteIcon />} label="Live Scheduling" variant="outlined" />
                </Stack>
                <Box sx={{ height: 520 }}>
                  <DataGrid
                    rows={rows}
                    getRowId={(row) => row._id}
                    columns={columns}
                    loading={loading}
                    disableRowSelectionOnClick
                    initialState={{
                      pagination: { paginationModel: { pageSize: 10 } },
                      sorting: {
                        sortModel: [{ field: 'startTime', sort: 'desc' }],
                      },
                    }}
                    pageSizeOptions={[10, 25, 50]}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <SessionFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        initialValues={selectedSession}
        onSubmit={handleDialogSubmit}
        loading={dialogLoading}
        educatorOptions={educators}
        studentOptions={students}
      />
    </DashboardLayout>
  );
};

export default SessionsPage;
